import type { ModelPosition, StockPosition } from '../../../../domain/positions';
import type { EditableField } from './position-item-renderer.chart';

/** The values the user changed in a row, by field, and only those: a field left as it was is not in it. */
export type PositionChanges = Partial<Readonly<Record<EditableField, number>>>;

/** A position a row can be edited for: a model, measured and counted, or a stock item, counted. */
export type EditablePosition = ModelPosition | StockPosition;

/**
 * The PositionItemRendererEvent is dispatched when the user saves what they changed in a model or stock position's row.
 *
 * It carries the position as it was when Enter was pressed and the changed values beside it, rather than a position with them written in: what the changes mean — whether the engine is asked again, and what a refusal takes back — is the machine's to decide, and it needs both to decide it. The row only dispatches it
 * with at least one change, so the changes are never empty — and for a stock position only ever its place and its count, the fields it is edited in.
 */
export default class PositionItemRendererEvent extends Event {
	/**
	 * The static POSITION_CHANGED type is dispatched when Enter saves a row being edited with something changed in it.
	 *
	 * Example: addEventListener(PositionItemRendererEvent.POSITION_CHANGED, (event: PositionItemRendererEvent) => {});
	 */
	public static POSITION_CHANGED = 'PositionItemRendererEvent.positionChanged';

	/** The values the user changed, by field. */
	public get changes(): PositionChanges {
		return this.#changes;
	}

	/** The position the row was showing when Enter was pressed. */
	public get position(): EditablePosition {
		return this.#position;
	}

	#changes: PositionChanges;
	#position: EditablePosition;

	public constructor(position: EditablePosition, changes: PositionChanges) {
		super(PositionItemRendererEvent.POSITION_CHANGED, { bubbles: true });
		this.#changes = changes;
		this.#position = position;
	}
}
